import { supabase } from "@/integrations/supabase/client";
import { 
  aiIntroductionContent, 
  machineLearningBasicsContent,
  nlpOverviewContent,
  computerVisionBasicsContent,
  aiEthicsContent
} from "../data/sample-module-content";

export async function seedAIFundamentalsContent() {
  try {
    console.log("Starting to seed AI Fundamentals modules content...");

    // Define our modules
    const modules = [
      {
        title: "Introduction to AI",
        description: "Understand the fundamentals of artificial intelligence and its business applications",
        difficulty: "Beginner",
        content: aiIntroductionContent
      },
      {
        title: "Machine Learning Basics",
        description: "Learn the core concepts of machine learning and how it powers AI systems",
        difficulty: "Beginner",
        content: machineLearningBasicsContent
      },
      {
        title: "Natural Language Processing",
        description: "Explore how computers understand and generate human language",
        difficulty: "Beginner",
        content: nlpOverviewContent
      },
      {
        title: "Computer Vision Fundamentals",
        description: "Discover how AI systems interpret and understand visual information",
        difficulty: "Beginner",
        content: computerVisionBasicsContent
      },
      {
        title: "AI Ethics & Responsible AI",
        description: "Understand the ethical considerations in developing and deploying AI systems",
        difficulty: "Beginner",
        content: aiEthicsContent
      }
    ];

    // Get the AI Fundamentals path ID or create if it doesn't exist
    let { data: pathData, error: pathError } = await supabase
      .from('learning_paths')
      .select('id')
      .eq('title', 'AI Fundamentals')
      .maybeSingle();

    if (pathError) {
      console.error("Error finding AI Fundamentals path:", pathError);
      return { success: false, error: pathError };
    }

    if (!pathData) {
      console.log("AI Fundamentals path not found, creating it...");
      // Create the path if it doesn't exist
      const { data: newPath, error: createPathError } = await supabase
        .from('learning_paths')
        .insert({
          title: 'AI Fundamentals',
          description: 'Learn the essential concepts of artificial intelligence and its applications',
          difficulty: 'Beginner',
          estimated_hours: 5
        })
        .select('id')
        .single();
      
      if (createPathError) {
        console.error("Error creating AI Fundamentals path:", createPathError);
        return { success: false, error: createPathError };
      }
      
      pathData = newPath;
    }

    const pathId = pathData.id;
    console.log(`Using AI Fundamentals path with ID: ${pathId}`);

    // Keep track of inserted or updated modules for debugging
    const moduleResults = [];

    // Create or update modules and associate them with the path
    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];
      
      // Check if module already exists with this title
      const { data: existingModule, error: findError } = await supabase
        .from('learning_modules')
        .select('id')
        .eq('title', module.title)
        .maybeSingle();

      if (findError) {
        console.error(`Error checking for existing module '${module.title}':`, findError);
        moduleResults.push({ module: module.title, status: 'error', error: findError.message });
        continue;
      }

      let moduleId;
      
      if (existingModule) {
        // Update existing module
        moduleId = existingModule.id;
        const { error: updateError } = await supabase
          .from('learning_modules')
          .update({
            description: module.description,
            difficulty: module.difficulty,
            content: module.content
          })
          .eq('id', moduleId);

        if (updateError) {
          console.error(`Error updating module '${module.title}':`, updateError);
          moduleResults.push({ module: module.title, status: 'update_error', error: updateError.message });
          continue;
        }
        
        console.log(`Updated module: ${module.title} with ID: ${moduleId}`);
        moduleResults.push({ module: module.title, status: 'updated', id: moduleId });
      } else {
        // Create new module
        const { data: newModule, error: createError } = await supabase
          .from('learning_modules')
          .insert({
            title: module.title,
            description: module.description,
            difficulty: module.difficulty,
            content: module.content
          })
          .select('id')
          .single();

        if (createError) {
          console.error(`Error creating module '${module.title}':`, createError);
          moduleResults.push({ module: module.title, status: 'create_error', error: createError.message });
          continue;
        }
        
        moduleId = newModule.id;
        console.log(`Created module: ${module.title} with ID: ${moduleId}`);
        moduleResults.push({ module: module.title, status: 'created', id: moduleId });
      }

      // Check if module is already associated with path
      const { data: existingPathModule, error: pathModuleError } = await supabase
        .from('learning_path_modules')
        .select('id')
        .eq('path_id', pathId)
        .eq('module_id', moduleId)
        .maybeSingle();

      if (pathModuleError) {
        console.error(`Error checking path module association:`, pathModuleError);
        moduleResults.push({ 
          module: module.title, 
          status: 'path_association_check_error', 
          error: pathModuleError.message 
        });
        continue;
      }

      if (!existingPathModule) {
        // Associate module with path if not already associated
        const { error: associateError } = await supabase
          .from('learning_path_modules')
          .insert({
            path_id: pathId,
            module_id: moduleId,
            order_index: i,
            is_required: true
          });

        if (associateError) {
          console.error(`Error associating module with path:`, associateError);
          moduleResults.push({ 
            module: module.title, 
            status: 'path_association_error', 
            error: associateError.message 
          });
          continue;
        }
        
        console.log(`Associated module '${module.title}' with AI Fundamentals path`);
        moduleResults.push({ 
          module: module.title, 
          status: 'associated', 
          moduleId,
          pathId
        });
      } else {
        console.log(`Module '${module.title}' already associated with path`);
        moduleResults.push({ 
          module: module.title, 
          status: 'already_associated', 
          moduleId,
          pathId
        });
      }
    }

    console.log("Seeding complete. Results:", moduleResults);
    return { success: true, results: moduleResults };
  } catch (error) {
    console.error("Error seeding AI Fundamentals content:", error);
    return { success: false, error };
  }
}
